// Lead Generation System — shared production-line pipeline logic.
//
// One source of truth for: the human status label + tone, the single NEXT
// one-click action per status, the terminal branches, the funnel roll-up, and
// the FE coaching grade. Keeping it here keeps HomeTab and PreleadsTab in
// lockstep with the backend PreleadStatus ladder.

import type { StatusTone } from "@/design-system/components/ui";
import {
  leadGeneratorApi,
  type PreleadDetailDto,
  type PreleadStatsDto,
  type PreleadStatus,
} from "../../api/leadGenerator";

export const STATUS_LABEL: Record<PreleadStatus, string> = {
  Draft: "New",
  MessageSent: "Message sent",
  ResponseReceived: "Response received",
  NdaRequested: "NDA requested",
  NdaSubmitted: "NDA complete",
  CimRequested: "CIM requested",
  CimReceived: "CIM received",
  Interested: "Interested",
  Escalated: "Escalated",
  Contacted: "Contacted",
  BadFit: "Bad fit",
  Duplicate: "Duplicate",
  NoResponse: "No response",
  Closed: "Closed",
};

export const STATUS_TONE: Record<PreleadStatus, StatusTone> = {
  Draft: "neutral",
  MessageSent: "progress",
  ResponseReceived: "progress",
  NdaRequested: "progress",
  NdaSubmitted: "progress",
  CimRequested: "progress",
  CimReceived: "progress",
  Interested: "ok",
  Escalated: "info",
  Contacted: "ok",
  BadFit: "muted",
  Duplicate: "muted",
  NoResponse: "muted",
  Closed: "muted",
};

/** Terminal branches — no forward action; the row is history. */
export const TERMINAL_STATUSES: readonly PreleadStatus[] = [
  "Escalated",
  "Contacted",
  "BadFit",
  "Duplicate",
  "NoResponse",
  "Closed",
];

export function isTerminal(status: PreleadStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

/** The single next one-click advance for a prelead, or null at a terminal
 *  status. `credits` flags advances that rack earnings (so the console can
 *  reload the authoritative weekly total after a successful click). */
export interface NextAction {
  label: string;
  run: (id: string) => Promise<PreleadDetailDto>;
  credits: boolean;
}

export function nextAction(status: PreleadStatus): NextAction | null {
  switch (status) {
    case "Draft":
      return { label: "Message sent", run: leadGeneratorApi.messageSent, credits: true };
    case "MessageSent":
      return { label: "Response received", run: leadGeneratorApi.responseReceived, credits: true };
    case "ResponseReceived":
      return { label: "Request NDA", run: leadGeneratorApi.requestNda, credits: true };
    case "NdaRequested":
      return { label: "NDA completed & sent", run: leadGeneratorApi.completeNda, credits: true };
    case "NdaSubmitted":
      return { label: "Request CIM", run: leadGeneratorApi.requestCim, credits: true };
    case "CimRequested":
      return { label: "CIM received", run: leadGeneratorApi.receiveCim, credits: true };
    case "CimReceived":
      return { label: "Mark interested", run: leadGeneratorApi.markInterested, credits: true };
    case "Interested":
      // Escalate is handled separately (it returns a lead id, not a prelead),
      // so no plain forward action here.
      return null;
    default:
      return null;
  }
}

/** Whether the NDA signed-file drop is offered at this status. */
export function offersNdaFile(status: PreleadStatus): boolean {
  return status === "ResponseReceived" || status === "NdaRequested" || status === "NdaSubmitted";
}

/** Whether the CIM file drop is offered at this status. */
export function offersCimFile(status: PreleadStatus): boolean {
  return status === "NdaSubmitted" || status === "CimRequested" || status === "CimReceived";
}

// ── Funnel ───────────────────────────────────────────────────────────────────

// The linear working order (terminal branches BadFit/Duplicate/NoResponse/Closed
// are excluded). A prelead sits in exactly one status, so "reached stage X" is
// the sum of the current counts at or beyond X in this order.
const LINEAR_ORDER: readonly PreleadStatus[] = [
  "Draft",
  "MessageSent",
  "ResponseReceived",
  "NdaRequested",
  "NdaSubmitted",
  "CimRequested",
  "CimReceived",
  "Interested",
  "Escalated",
  "Contacted",
];

function reachedAtLeast(stats: PreleadStatsDto, stage: PreleadStatus): number {
  const from = LINEAR_ORDER.indexOf(stage);
  if (from < 0) return 0;
  let sum = 0;
  for (let i = from; i < LINEAR_ORDER.length; i += 1) {
    sum += stats.byStatus[LINEAR_ORDER[i]] ?? 0;
  }
  return sum;
}

export interface FunnelStage {
  key: string;
  label: string;
  count: number;
}

/** The console funnel: created → NDA → CIM → interested → escalated → contacted. */
export function buildFunnel(stats: PreleadStatsDto): FunnelStage[] {
  return [
    { key: "created", label: "Created", count: stats.total },
    { key: "nda", label: "Reached NDA", count: reachedAtLeast(stats, "NdaRequested") },
    { key: "cim", label: "Reached CIM", count: reachedAtLeast(stats, "CimRequested") },
    { key: "interested", label: "Interested", count: reachedAtLeast(stats, "Interested") },
    { key: "escalated", label: "Escalated", count: reachedAtLeast(stats, "Escalated") },
    { key: "contacted", label: "Contacted", count: reachedAtLeast(stats, "Contacted") },
  ];
}

/** Interested per 100 created — the console's headline conversion ratio. */
export function interestedPer100(stats: PreleadStatsDto): number | null {
  if (stats.total <= 0) return null;
  return Math.round((reachedAtLeast(stats, "Interested") / stats.total) * 100);
}

// ── Coaching grade ───────────────────────────────────────────────────────────

export type CoachingGrade = "A" | "B" | "C" | "D" | "F";

/** A FE-only coaching signal derived from the interested-conversion rate. This
 *  is NOT the manager-set pay grade (the console has no endpoint for that) —
 *  it's a self-check to steer the day's work. Null until there's enough volume
 *  to be meaningful. */
export function coachingGrade(stats: PreleadStatsDto): CoachingGrade | null {
  if (stats.total < 5) return null;
  const rate = reachedAtLeast(stats, "Interested") / stats.total;
  if (rate >= 0.25) return "A";
  if (rate >= 0.15) return "B";
  if (rate >= 0.08) return "C";
  if (rate >= 0.03) return "D";
  return "F";
}
